import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, ResolveFn, Router, RouterStateSnapshot } from "@angular/router";
import { AuthService } from "@auth0/auth0-angular";
import { EMPTY, Observable, switchMap } from "rxjs";
import { environment } from "src/environments/environment";
import { EVEstateType, EVOwnershipSubType, UnitAttributes, UnitNavigationInfoDto } from "src/models/units/unit-attributes.model";
import { UnitDataService } from "src/services/unit-data.service";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";
import { ResolverHelper } from "./resolver.helper";

// Enheter med disse undertypene vises på bruksenhet-siden, ikke på eiendomssiden.
const FUNCTIONAL_UNIT_OWNERSHIP_SUB_TYPES = [
    EVOwnershipSubType.FunctionalUnitAssumedRental,
    EVOwnershipSubType.StockCooperativeUnit,
    EVOwnershipSubType.BondCooperativeUnit,
];

// Borettslag, aksjelag og obligasjonslag vises alltid på eiendomssiden, uavhengig av estateType og næringsrolle.
const ESTATE_OWNERSHIP_SUB_TYPES = [
    EVOwnershipSubType.HousingCooperative,
    EVOwnershipSubType.StockCooperative,
    EVOwnershipSubType.BondCooperative,
];

export const InfopageResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> => {
    const router = inject(Router);
    const queryParams = route.queryParams;
    const hasKnr = queryParams.hasOwnProperty("knr");
    const hasBnr = queryParams.hasOwnProperty("bnr");
    const hasGnr = queryParams.hasOwnProperty("gnr");
    const hasOrgnumber = queryParams.hasOwnProperty("organizationNumber");
    const hasShareNumber = queryParams.hasOwnProperty("shareNumber");
    const hasUnitID = queryParams.hasOwnProperty("unitID") || queryParams.hasOwnProperty("unitId");
    const hasFunctionalUnitID = queryParams.hasOwnProperty("functionalUnitID");
    const unitDataService = inject(UnitDataService);
    const authService = inject(AuthService);

    if (hasOrgnumber) {
        const organizationNumber: string = queryParams["organizationNumber"] != null ? queryParams["organizationNumber"].toString() : "";
        const shareNumber: string = queryParams["shareNumber"] != null ? queryParams["shareNumber"].toString() : "";

        const searchString = organizationNumber + " " + shareNumber;
        return unitDataService.getUnitAttributes(searchString).pipe(
            switchMap((units: UnitAttributes[]) => {
                if (units.length > 0) {
                    return ResolverHelper.hasPermission(authService, [EVServiceType.EvWeb_EntireHousingCooperative]).pipe(
                        switchMap((hasHBRLRole) => {
                            if (hasHBRLRole && !hasShareNumber) {
                                const url = `${environment.evwebUrl}/infopage/housingcooperative;orgNumber=${units[0].organizationNumber}`;
                                window.open(url, "_self");
                            } else if (hasShareNumber) {
                                const url = `${environment.evwebUrl}/infopage/estate;orgNumber=${units[0].organizationNumber};shareNumber=${units[0].shareNumber};estateId=${units[0].estateID};addressId=${units[0].addressID}`;
                                window.open(url, "_self");
                            } else {
                                return redirectToSearchPage();
                            }
                            return EMPTY;
                        }),
                    );
                } else {
                    return redirectToSearchPage();
                }
            }),
        );
    } else if (hasKnr && hasBnr && hasGnr) {
        const KNr: string = queryParams["knr"] != null ? queryParams["knr"].toString() : "";
        const BNr: string = queryParams["bnr"] != null ? queryParams["bnr"].toString() : "";
        const GNr: string = queryParams["gnr"] != null ? queryParams["gnr"].toString() : "";
        const FNr: string = queryParams["fnr"] != null ? queryParams["fnr"].toString() : "";
        const SNr: string = queryParams["snr"] != null ? queryParams["snr"].toString() : "";

        const searchString = KNr + " " + GNr + " " + BNr + " " + FNr + " " + SNr;

        return unitDataService.getUnitAttributes(searchString).pipe(
            switchMap((units: UnitAttributes[]) => {
                if (units.length === 1) {
                    // UnitAttributes har ikke ownershipSubType, så vi henter navigasjonsinfo for enheten.
                    return redirectByUnitId(Number(units[0].unitId));
                } else {
                    return redirectToSearchPage();
                }
            }),
        );
    } else if (hasUnitID) {
        const unitId = Number(queryParams["unitID"] ?? queryParams["unitId"] ?? 0);
        return redirectByUnitId(unitId);
    } else if (hasFunctionalUnitID) {
        const functionalUnitID: string = queryParams["functionalUnitID"] != null ? queryParams["functionalUnitID"].toString() : "";
        redirectToStockUnitInfoPage(Number(functionalUnitID));
        return EMPTY;
    } else {
        return redirectToSearchPage();
    }

    function redirectToSearchPage() {
        // Fallback: no valid search params -> frontpage only.
        let url = environment.evwebUrl + "/frontpage";

        // Open search page when we have enough query information to perform a search.
        if (hasKnr && hasBnr && hasGnr) {
            const KNr = queryParams["knr"]?.toString() ?? "";
            const BNr = queryParams["bnr"]?.toString() ?? "";
            const GNr = queryParams["gnr"]?.toString() ?? "";
            const FNr = queryParams["fnr"]?.toString() ?? "";
            const SNr = queryParams["snr"]?.toString() ?? "";
            const queryString = `${KNr}%20${GNr}%20${BNr}%20${FNr}%20${SNr}`;
            url = `${environment.evwebUrl}/searchpage;searchString=${queryString};doSearch=true;`;
        } else if (hasOrgnumber) {
            const orgNr = queryParams["organizationNumber"]?.toString() ?? "";
            const shareNumber = queryParams["shareNumber"]?.toString() ?? "";
            const queryString = `${orgNr}%20${shareNumber}`;
            url = `${environment.evwebUrl}/searchpage;searchString=${queryString};doSearch=true;`;
        } else {
            return ResolverHelper.redirectToFrontPage(authService, router);
        }
        window.open(url, "_self");
        return EMPTY;
    }

    function redirectToEstateInfoPage(estateId: number, addressId: number, organizationNumber: number, shareNumber: number) {
        const url =
            environment.evwebUrl +
            "/infopage/estate;estateId=" +
            estateId +
            ";addressId=" +
            addressId +
            ";orgNumber=" +
            organizationNumber +
            ";shareNumber=" +
            shareNumber;
        window.open(url, "_self");
    }
    function redirectToCommercialInfoPage(estateId: number, addressId: number) {
        const url = environment.evwebUrl + "/infopage/commercial;estateId=" + estateId + ";addressId=" + addressId;
        window.open(url, "_self");
    }
    function redirectToStockUnitInfoPage(unitId: number) {
        router.navigate(["/infopage/functionalunit/" + unitId]);
    }

    function redirectByUnitId(unitId: number): Observable<any> {
        return unitDataService.getUnitNavigationInfoObservable(unitId).pipe(
            switchMap((unit: UnitNavigationInfoDto) => {
                if (unit != null) {
                    return redirectBasedOnEstateTypeAndPermission(
                        unitId,
                        unit.ownershipSubType,
                        unit.estateType,
                        unit.estateId,
                        unit.addressId,
                        unit.organizationNumber,
                        unit.shareNumber,
                    );
                } else {
                    return redirectToSearchPage();
                }
            }),
        );
    }

    function redirectBasedOnEstateTypeAndPermission(
        unitId: number,
        ownershipSubType: EVOwnershipSubType,
        estateType: EVEstateType,
        estateId: number,
        addressId: number,
        organizationNumber: number,
        shareNumber: number,
    ): any {
        if (FUNCTIONAL_UNIT_OWNERSHIP_SUB_TYPES.includes(ownershipSubType)) {
            redirectToStockUnitInfoPage(unitId);
            return EMPTY;
        } else if (ESTATE_OWNERSHIP_SUB_TYPES.includes(ownershipSubType)) {
            redirectToEstateInfoPage(estateId, addressId, organizationNumber, shareNumber);
            return EMPTY;
        } else {
            return ResolverHelper.hasPermission(authService, [EVServiceType.EvWeb_Commercial]).pipe(
                switchMap((hasCommercialRole) => {
                    if (
                        estateType != EVEstateType.Enebolig &&
                        estateType != EVEstateType.Rekkehus &&
                        estateType != EVEstateType.Leilighet &&
                        estateType != EVEstateType.Fritidsbolig &&
                        estateType != EVEstateType.Tomannsbolig &&
                        hasCommercialRole
                    ) {
                        redirectToCommercialInfoPage(estateId, addressId);
                    } else {
                        redirectToEstateInfoPage(estateId, addressId, organizationNumber, shareNumber);
                    }
                    return EMPTY;
                }),
            );
        }
    }
};
